import React, { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { FaTimes, FaClock, FaSave, FaCopy, FaPlus, FaMinus, FaUser, FaUserTie, FaUtensils, FaGlassWhiskey } from 'react-icons/fa';
import './SchedulePopup.css';

const dayMapping = {
  'Monday': 'Lundi',
  'Tuesday': 'Mardi',
  'Wednesday': 'Mercredi',
  'Thursday': 'Jeudi',
  'Friday': 'Vendredi',
  'Saturday': 'Samedi',
  'Sunday': 'Dimanche'
};

const SchedulePopup = ({ employee, onClose, existingSchedule, updateSchedule, selectedDay }) => {
  const [shifts, setShifts] = useState([{ start: '', end: '' }]);
  const [loading, setLoading] = useState(false);
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Fonction pour obtenir l'icône du rôle
  const getRoleIcon = (role) => {
    switch (role) {
      case 'server':
        return FaUserTie;
      case 'cook':
        return FaUtensils;
      case 'bar':
        return FaGlassWhiskey;
      default:
        return FaUser;
    }
  };

  // Fonction pour obtenir la couleur du rôle
  const getRoleColor = (role) => {
    switch (role) {
      case 'server':
        return '#3b82f6';
      case 'cook':
        return '#f59e0b';
      case 'bar':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const RoleIcon = getRoleIcon(employee.role);

  useEffect(() => {
    // Sauvegarder la position de défilement actuelle
    const scrollPosition = window.scrollY;
    document.body.classList.add('popup-open');
    // Appliquer la position de défilement comme margin-top
    document.body.style.top = `-${scrollPosition}px`;
    
    return () => {
      // Restaurer la position de défilement
      document.body.classList.remove('popup-open');
      document.body.style.top = '';
      window.scrollTo(0, scrollPosition);
    };
  }, []);

  useEffect(() => {
    if (existingSchedule) {
      const currentSchedule = existingSchedule.schedule[selectedDay];
      if (currentSchedule && currentSchedule !== 'Ne travaille pas') {
        // Convertir le format existant en tableau de shifts
        const existingShifts = currentSchedule.split(' / ').map(shift => {
          const [start, end] = shift.split(' - ');
          return { start, end };
        });
        setShifts(existingShifts);
      } else {
        setShifts([{ start: '', end: '' }]);
      }
    }
  }, [existingSchedule, selectedDay]);

  const handleShiftChange = (index, field, value) => {
    const newShifts = [...shifts];
    newShifts[index][field] = value;
    setShifts(newShifts);
  };

  const addShift = () => {
    setShifts([...shifts, { start: '', end: '' }]);
  };

  const removeShift = (index) => {
    if (shifts.length > 1) {
      const newShifts = shifts.filter((_, i) => i !== index);
      setShifts(newShifts);
    }
  };

  const handleSubmit = async (e, applyToAll = false) => {
    e.preventDefault();
    setLoading(true);

    // Vérifier si tous les champs sont vides
    const isEmpty = shifts.every(shift => !shift.start && !shift.end);
    const scheduleValue = isEmpty ? 'Ne travaille pas' : 
      shifts.map(shift => `${shift.start} - ${shift.end}`).join(' / ');

    try {
      const scheduleRef = doc(db, 'horaires', employee.id);
      let updatedSchedule;

      if (applyToAll) {
        updatedSchedule = daysOfWeek.reduce((acc, day) => ({
          ...acc,
          [day]: scheduleValue
        }), {});
      } else {
        updatedSchedule = {
          ...existingSchedule?.schedule,
          [selectedDay]: scheduleValue
        };
      }

      await setDoc(scheduleRef, {
        employeeId: employee.id,
        firstName: employee.firstName,
        lastName: employee.lastName,
        role: employee.role,
        schedule: updatedSchedule,
      });

      updateSchedule(employee.id, {
        ...existingSchedule,
        schedule: updatedSchedule,
      });

      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement des horaires:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="schedule-popup-overlay">
      <div className="schedule-popup-content">
        {/* Header */}
        <div className="schedule-popup-header">
          <div className="schedule-popup-employee-info">
            <div className="schedule-popup-avatar" style={{ backgroundColor: getRoleColor(employee.role) }}>
              <RoleIcon />
            </div>
            <div className="schedule-popup-employee-details">
              <h3 className="schedule-popup-employee-name">
                {employee.firstName} {employee.lastName}
              </h3>
              <div className="schedule-popup-employee-role">
                <RoleIcon className="schedule-popup-role-icon" />
                <span>{employee.role === 'server' ? 'Serveur' : employee.role === 'cook' ? 'Cuisinier' : employee.role === 'bar' ? 'Barman' : employee.role}</span>
              </div>
            </div>
          </div>
          <button className="schedule-popup-close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Jour sélectionné */}
        <div className="schedule-popup-day-section">
          <div className="schedule-popup-day-header">
            <FaClock className="schedule-popup-day-icon" />
            <h4 className="schedule-popup-day-title">{dayMapping[selectedDay]}</h4>
          </div>
        </div>

        {/* Horaires */}
        <div className="schedule-popup-shifts-section">
          <h5 className="schedule-popup-shifts-title">Horaires de travail</h5>
          
          {shifts.map((shift, index) => (
            <div key={index} className="schedule-popup-shift-container">
              <div className="schedule-popup-shift-inputs">
                <div className="schedule-popup-time-group">
                  <label className="schedule-popup-time-label">Début</label>
                  <input
                    type="time"
                    value={shift.start}
                    onChange={(e) => handleShiftChange(index, 'start', e.target.value)}
                    className="schedule-popup-time-input"
                  />
                </div>
                
                <div className="schedule-popup-time-separator">à</div>
                
                <div className="schedule-popup-time-group">
                  <label className="schedule-popup-time-label">Fin</label>
                  <input
                    type="time"
                    value={shift.end}
                    onChange={(e) => handleShiftChange(index, 'end', e.target.value)}
                    className="schedule-popup-time-input"
                  />
                </div>
                
                {shifts.length > 1 && (
                  <button 
                    type="button" 
                    className="schedule-popup-remove-shift"
                    onClick={() => removeShift(index)}
                  >
                    <FaMinus />
                  </button>
                )}
              </div>
            </div>
          ))}

          <button type="button" className="schedule-popup-add-shift" onClick={addShift}>
            <FaPlus />
            <span>Ajouter une coupure</span>
          </button>
        </div>

        {/* Actions */}
        <div className="schedule-popup-actions">
          <button 
            type="button" 
            className="schedule-popup-save-btn"
            onClick={(e) => handleSubmit(e, false)}
            disabled={loading}
          >
            <FaSave />
            <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
          </button>
          
          <button 
            type="button" 
            className="schedule-popup-apply-all-btn"
            onClick={(e) => handleSubmit(e, true)}
            disabled={loading}
          >
            <FaCopy />
            <span>Appliquer à tous les jours</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SchedulePopup;
